import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, formatQuoteDate } from '../../utils/quotes';
import { watchInvoicesForClient, isOverdue, KIND_LABELS } from '../../utils/invoices';
import { downloadInvoice } from '../../utils/invoicePdf';
import { MONO } from '../../constants/fonts';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  badge: ds.badge,
  empty: { ...ds.empty, margin: 0 },
  error: ds.error,
  summary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '14px 16px',
    marginBottom: 16,
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
  },
  summaryLabel: {
    fontSize: '0.8rem',
    color: '#777',
  },
  summaryValue: {
    fontFamily: MONO,
    fontSize: '1.15rem',
    fontWeight: 700,
  },
  // Wraps on narrow screens so the description keeps its width and the
  // controls drop below, rather than the text being squeezed to a column.
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    padding: '14px 0',
    borderTop: '1px solid #ececE6',
  },
  main: {
    flex: '1 1 220px',
    minWidth: 0,
  },
  reference: {
    fontFamily: MONO,
    fontSize: '0.8rem',
    color: '#888',
  },
  desc: {
    fontSize: '0.88rem',
    color: '#222',
    marginTop: 2,
  },
  meta: {
    fontSize: '0.75rem',
    color: '#888',
    marginTop: 3,
  },
  overdue: {
    color: '#c0392b',
    fontWeight: 600,
  },
  amount: {
    fontFamily: MONO,
    fontSize: '0.95rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  download: {
    padding: '7px 11px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: '0.85rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
  allClear: {
    fontSize: '0.85rem',
    color: '#777',
    margin: '0 0 4px',
    paddingTop: 4,
  },
  paidToggle: {
    width: '100%',
    marginTop: 16,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  bankBox: {
    marginTop: 20,
    padding: '14px 16px',
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
  },
  bankLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#888',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  bankRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '4px 0',
    fontSize: '0.85rem',
    color: '#333',
  },
  bankValue: {
    fontFamily: MONO,
  },
  bankNote: {
    fontSize: '0.75rem',
    color: '#999',
    lineHeight: 1.5,
    margin: '10px 0 0',
  },
};

const BADGE_COLORS = {
  unpaid: { background: '#fef3c7', color: '#92400e' },
  paid: { background: '#d1fae5', color: '#065f46' },
  overdue: { background: '#fee2e2', color: '#991b1b' },
};

export default function InvoicesCard() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaid, setShowPaid] = useState(false);

  useEffect(() => {
    if (!user?.email) return undefined;
    return watchInvoicesForClient(
      user.email,
      (docs) => { setInvoices(docs); setLoading(false); },
      (err) => { setError(err.message.replace('Firebase: ', '')); setLoading(false); },
    );
  }, [user?.email]);

  // Paid invoices are history — kept, but folded away so what's actually due
  // isn't buried under months of settled care plan bills.
  const unpaid = invoices.filter((invoice) => invoice.status !== 'paid');
  const paid = invoices.filter((invoice) => invoice.status === 'paid');
  const outstanding = unpaid.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
  const overdueCount = unpaid.filter(isOverdue).length;

  // Taken from the most recent invoice rather than from constants: what a
  // client should pay is whatever was on the invoice they were sent.
  const bank = invoices[0]?.business?.bank;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Invoices</div>
      <p style={styles.description}>Everything we've billed you for, and what's still outstanding.</p>

      {error && <div style={styles.error}>{error}</div>}
      {!error && loading && <p style={styles.empty}>Loading…</p>}
      {!error && !loading && invoices.length === 0 && (
        <p style={styles.empty}>No invoices yet.</p>
      )}

      {invoices.length > 0 && (
        <>
          <div style={styles.summary}>
            <span style={styles.summaryLabel}>
              Outstanding
              {overdueCount > 0 && (
                <span style={styles.overdue}> · {overdueCount} overdue</span>
              )}
            </span>
            <span style={styles.summaryValue}>{formatMoney(outstanding)}</span>
          </div>

          {unpaid.length === 0 && (
            <p style={styles.allClear}>Nothing outstanding — you're all paid up.</p>
          )}

          {(showPaid ? invoices : unpaid).map((invoice) => {
            const overdue = isOverdue(invoice);
            const state = invoice.status === 'paid' ? 'paid' : overdue ? 'overdue' : 'unpaid';
            return (
              <div key={invoice.id} style={styles.row}>
                <div style={styles.main}>
                  <div style={styles.reference}>{invoice.reference}</div>
                  <div style={styles.desc}>{invoice.description}</div>
                  <div style={{ ...styles.meta, ...(overdue ? styles.overdue : {}) }}>
                    {KIND_LABELS[invoice.kind] || ''}
                    {invoice.status === 'paid'
                      ? ` · paid ${formatQuoteDate(invoice.paidAt) || ''}`
                      : ` · due ${formatQuoteDate(invoice.dueAt) || ''}${overdue ? ' · overdue' : ''}`}
                  </div>
                </div>
                <span style={styles.amount}>{formatMoney(invoice.amount)}</span>
                <span style={{ ...styles.badge, ...BADGE_COLORS[state] }}>
                  {state === 'overdue' ? 'Overdue' : state === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
                <button
                  type="button"
                  style={styles.download}
                  onClick={() => downloadInvoice(invoice)}
                  aria-label={`Download ${invoice.reference}`}
                  title="Download PDF"
                >
                  ⤓
                </button>
              </div>
            );
          })}

          {paid.length > 0 && (
            <button type="button" style={styles.paidToggle} onClick={() => setShowPaid((prev) => !prev)}>
              {showPaid ? 'Hide' : 'View'} paid invoices ({paid.length})
            </button>
          )}

          {bank && (
            <div style={styles.bankBox}>
              <div style={styles.bankLabel}>How to pay</div>
              <div style={styles.bankRow}>
                <span>Account name</span><span style={styles.bankValue}>{bank.accountName}</span>
              </div>
              <div style={styles.bankRow}>
                <span>Sort code</span><span style={styles.bankValue}>{bank.sortCode}</span>
              </div>
              <div style={styles.bankRow}>
                <span>Account number</span><span style={styles.bankValue}>{bank.accountNumber}</span>
              </div>
              <p style={styles.bankNote}>
                Quote the invoice reference with your payment. If bank details you've been sent by
                email differ from these, contact us before paying.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
